import { FC, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  CheckCircle,
  ClipboardList,
  Heart,
  RefreshCw,
  AlertTriangle,
  Calendar,
  Dumbbell,
  Trophy,
  FlaskConical,
  ChevronRight,
} from "lucide-react";
import { Skeleton } from "@chakra-ui/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/it";
import { useEndpoints, INotificationItem } from "../../hooks/useEndpoints";
import Typhography from "../../shared/Typhography/Typhography";
import { CustomBadge } from "../../shared/CustomBadge/CustomBadge";
import { CompileEventModal } from "../../components/CalendarPage/CompileEventModal/CompileEventModal";
import { EVENT_TYPES } from "../../enums/event";
import style from "./NotificationsPage.module.scss";

dayjs.extend(relativeTime);
dayjs.locale("it");

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelativeDate(dateStr: string): string {
  const d = dayjs(dateStr);
  const now = dayjs();
  const diffDays = now.diff(d, "day");
  if (diffDays === 0) return "Oggi";
  if (diffDays === 1) return "Ieri";
  if (diffDays > 1 && diffDays < 7) return `${diffDays} giorni fa`;
  // Date future (diffDays negativo) o oltre 6 giorni fa: format completo
  return d.format("DD MMM YYYY");
}

type EventGroup = {
  key: string;
  label: string;
  icon: React.ReactNode;
  badgeColor: string;
  events: any[];
};

// ─── Banner reminder ─────────────────────────────────────────────────────────

const ReminderBanner: FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  variant?: "warning" | "info";
}> = ({ icon, title, description, variant = "warning" }) => (
  <div className={`${style.banner} ${style[`banner--${variant}`]}`}>
    <div className={style.bannerIconWrap}>{icon}</div>
    <div className={style.bannerText}>
      <Typhography size="bold-14">{title}</Typhography>
      <Typhography size="regular-12" className={style.bannerDesc}>
        {description}
      </Typhography>
    </div>
    <ChevronRight size={16} className={style.bannerChevron} />
  </div>
);

// ─── Event notification card ─────────────────────────────────────────────────

const EventCard: FC<{
  event: any;
  onCompile: (event: any) => void;
}> = ({ event, onCompile }) => {
  const startDate = event.startDate || event.date;
  const isWorkout = event.type === EVENT_TYPES.WORKOUT;
  const isMatch = event.type === EVENT_TYPES.MATCH;

  const typeLabel = isWorkout ? "Allenamento" : isMatch ? "Gara" : "Test";
  const badgeColor = isWorkout
    ? "light-blue-dust"
    : isMatch
    ? "lightblue"
    : "lightgreen";

  const TypeIcon = isWorkout ? Dumbbell : isMatch ? Trophy : FlaskConical;

  return (
    <div className={style.eventCard} onClick={() => onCompile(event)}>
      <div className={style.eventCardIcon}>
        <TypeIcon size={18} strokeWidth={2} />
      </div>
      <div className={style.eventCardInfo}>
        <div className={style.eventCardTopRow}>
          <CustomBadge label={typeLabel} color={badgeColor} />
          <Typhography size="bold-14">
            {event.name || event.testType || "—"}
          </Typhography>
        </div>
        <div className={style.eventCardMeta}>
          {startDate && (
            <span className={style.eventCardMetaItem}>
              <Calendar size={12} strokeWidth={2} />
              {formatRelativeDate(startDate)}
              {event.startDate &&
                event.endDate &&
                ` · ${dayjs(event.startDate).format("HH:mm")} – ${dayjs(
                  event.endDate
                ).format("HH:mm")}`}
            </span>
          )}
          {event.location && (
            <span className={style.eventCardMetaItem}>{event.location}</span>
          )}
        </div>
      </div>
      <button className={style.compileButton}>
        <ClipboardList size={14} strokeWidth={2.5} />
        Compila
      </button>
    </div>
  );
};

// ─── Empty state ─────────────────────────────────────────────────────────────

const EmptyState: FC = () => (
  <div className={style.emptyState}>
    <div className={style.emptyIconWrap}>
      <CheckCircle size={28} strokeWidth={1.5} />
    </div>
    <Typhography size="bold-16">Tutto in ordine!</Typhography>
    <Typhography size="regular-14" className={style.emptyDesc}>
      Non hai notifiche in sospeso. Ottimo lavoro!
    </Typhography>
  </div>
);

// ─── Section header ──────────────────────────────────────────────────────────

const SectionHeader: FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
}> = ({ icon, label, count }) => (
  <div className={style.sectionHeader}>
    <div className={style.sectionHeaderLeft}>
      {icon}
      <Typhography size="bold-14">{label}</Typhography>
    </div>
    <span className={style.sectionCount}>{count}</span>
  </div>
);

// ─── Main page ───────────────────────────────────────────────────────────────

const NotificationsPage: FC = () => {
  const { getNotifications } = useEndpoints();
  const [compileEvent, setCompileEvent] = useState<any>(undefined);
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data } = await getNotifications();
      return data;
    },
  });

  const raw = query.data;
  const items: INotificationItem[] = Array.isArray(raw)
    ? raw
    : ((raw as any)?.notifications ?? []);
  const isLoading = query.isPending;

  const eventNotifications = items.filter((n: INotificationItem) => n.type === "event-to-compile");
  const showFrc = items.some((n: INotificationItem) => n.type === "frc-to-compile");
  const showHrv = items.some((n: INotificationItem) => n.type === "hrv-to-compile");
  const showHooper = items.some((n: INotificationItem) => n.type === "hooper-to-compile");

  const reminderCount =
    (showFrc ? 1 : 0) + (showHrv ? 1 : 0) + (showHooper ? 1 : 0);
  const totalCount = eventNotifications.length + reminderCount;
  const hasAnything = totalCount > 0;

  // Group events by type
  const eventGroups: EventGroup[] = useMemo(() => {
    const sorted = [...eventNotifications].sort(
      (a, b) => a.priority - b.priority
    );
    const groups: EventGroup[] = [];

    const workouts = sorted.filter(
      (n) => n.data?.type === EVENT_TYPES.WORKOUT
    );
    const matches = sorted.filter((n) => n.data?.type === EVENT_TYPES.MATCH);
    const tests = sorted.filter(
      (n) =>
        n.data?.type === EVENT_TYPES.TEST ||
        n.data?.type === EVENT_TYPES.SYSTEM_TEST_EVENT
    );

    if (workouts.length > 0) {
      groups.push({
        key: "workout",
        label: "Allenamenti da compilare",
        icon: <Dumbbell size={16} strokeWidth={2.5} />,
        badgeColor: "#1a8fb5",
        events: workouts.map((n) => n.data),
      });
    }
    if (matches.length > 0) {
      groups.push({
        key: "match",
        label: "Gare da compilare",
        icon: <Trophy size={16} strokeWidth={2.5} />,
        badgeColor: "#3949ab",
        events: matches.map((n) => n.data),
      });
    }
    if (tests.length > 0) {
      groups.push({
        key: "test",
        label: "Test da compilare",
        icon: <FlaskConical size={16} strokeWidth={2.5} />,
        badgeColor: "#1a8f6e",
        events: tests.map((n) => n.data),
      });
    }

    return groups;
  }, [eventNotifications]);

  // Filter
  const filteredGroups = useMemo(() => {
    if (typeFilter === "all") return eventGroups;
    return eventGroups.filter((g) => g.key === typeFilter);
  }, [eventGroups, typeFilter]);

  const filterCounts = useMemo(
    () => ({
      all: eventNotifications.length,
      workout: eventGroups.find((g) => g.key === "workout")?.events.length ?? 0,
      match: eventGroups.find((g) => g.key === "match")?.events.length ?? 0,
      test: eventGroups.find((g) => g.key === "test")?.events.length ?? 0,
    }),
    [eventNotifications, eventGroups]
  );

  return (
    <div className={style.container}>
      {/* Header */}
      <div className={style.header}>
        <div className={style.headerLeft}>
          <div className={style.headerIconWrapper}>
            <Bell size={20} strokeWidth={2} />
          </div>
          <div>
            <Typhography type="h1" size="bold-24">
              Notifiche
            </Typhography>
            <Typhography size="regular-14" className={style.subtitle}>
              Attività in sospeso da completare
            </Typhography>
          </div>
        </div>
        {!isLoading && hasAnything && (
          <div className={style.countBadge}>
            <Typhography size="bold-12">{totalCount}</Typhography>
          </div>
        )}
      </div>

      {/* Error */}
      {query.isError && (
        <div className={style.errorState}>
          <div className={style.errorIconWrap}>
            <AlertTriangle size={28} strokeWidth={1.5} />
          </div>
          <Typhography size="bold-16">Errore nel caricamento</Typhography>
          <button className={style.retryButton} onClick={() => query.refetch()}>
            <RefreshCw size={14} strokeWidth={2.5} />
            Riprova
          </button>
        </div>
      )}

      {/* Skeletons */}
      {isLoading && (
        <div className={style.sections}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height="72px" borderRadius="12px" />
          ))}
        </div>
      )}

      {/* Content */}
      {!isLoading && !query.isError && (
        <>
          {!hasAnything && <EmptyState />}

          {hasAnything && (
            <div className={style.sections}>
              {/* Reminder banners */}
              {reminderCount > 0 && (
                <div className={style.section}>
                  <SectionHeader
                    icon={<AlertTriangle size={16} strokeWidth={2.5} />}
                    label="Promemoria"
                    count={reminderCount}
                  />
                  <div className={style.sectionContent}>
                    {showHooper && (
                      <ReminderBanner
                        icon={<ClipboardList size={20} strokeWidth={2} />}
                        title="Questionario soggettivo da compilare"
                        description="Compila il questionario giornaliero di readiness soggettiva"
                        variant="warning"
                      />
                    )}
                    {showFrc && (
                      <ReminderBanner
                        icon={<Heart size={20} strokeWidth={2} />}
                        title="Metrica FRC da registrare"
                        description="Non hai ancora inserito la metrica FRC di oggi"
                        variant="info"
                      />
                    )}
                    {showHrv && (
                      <ReminderBanner
                        icon={<Heart size={20} strokeWidth={2} />}
                        title="Metrica HRV da registrare"
                        description="Non hai ancora inserito la metrica HRV di oggi"
                        variant="info"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Event filter pills */}
              {eventNotifications.length > 0 && eventGroups.length > 1 && (
                <div className={style.filterPills}>
                  <button
                    className={`${style.filterPill} ${
                      typeFilter === "all" ? style.filterPillActive : ""
                    }`}
                    onClick={() => setTypeFilter("all")}
                  >
                    Tutti
                    <span className={style.pillCount}>
                      {filterCounts.all}
                    </span>
                  </button>
                  {filterCounts.workout > 0 && (
                    <button
                      className={`${style.filterPill} ${
                        typeFilter === "workout" ? style.filterPillActive : ""
                      }`}
                      onClick={() => setTypeFilter("workout")}
                    >
                      <Dumbbell size={13} strokeWidth={2.5} />
                      Allenamenti
                      <span className={style.pillCount}>
                        {filterCounts.workout}
                      </span>
                    </button>
                  )}
                  {filterCounts.match > 0 && (
                    <button
                      className={`${style.filterPill} ${
                        typeFilter === "match" ? style.filterPillActive : ""
                      }`}
                      onClick={() => setTypeFilter("match")}
                    >
                      <Trophy size={13} strokeWidth={2.5} />
                      Gare
                      <span className={style.pillCount}>
                        {filterCounts.match}
                      </span>
                    </button>
                  )}
                  {filterCounts.test > 0 && (
                    <button
                      className={`${style.filterPill} ${
                        typeFilter === "test" ? style.filterPillActive : ""
                      }`}
                      onClick={() => setTypeFilter("test")}
                    >
                      <FlaskConical size={13} strokeWidth={2.5} />
                      Test
                      <span className={style.pillCount}>
                        {filterCounts.test}
                      </span>
                    </button>
                  )}
                </div>
              )}

              {/* Event groups */}
              {filteredGroups.map((group) => (
                <div key={group.key} className={style.section}>
                  <SectionHeader
                    icon={group.icon}
                    label={group.label}
                    count={group.events.length}
                  />
                  <div className={style.sectionContent}>
                    {group.events.map((event, i) => (
                      <EventCard
                        key={event?._id ?? i}
                        event={event}
                        onCompile={setCompileEvent}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <CompileEventModal
        isOpen={compileEvent}
        onClose={() => {
          setCompileEvent(undefined);
          query.refetch();
        }}
      />
    </div>
  );
};

export default NotificationsPage;
